import React from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { muiTheme } from '@avangard/ui/theme';
import { black, gray } from '@avangard/ui/colors';
import { breakpoints } from '@avangard/ui/utils';

import { i18n } from '@lib/i18n';
import { CommonHelper } from '@helpers';
import { PieChart } from '@modules/shared/organisms';
import { TabsNavigation, TabNavigation } from '@modules/layout/moleculas';
import {
    maleColorsByAge,
    femaleColorsByAge,
    nogenderColorsByAge,
} from '@modules/summary-results/constants';
import { ProgressItem } from '@modules/charts/moleculas';
import { Gender } from '@modules/types/graphql';

import type { CallbackArgs as PieDataCallbackArgs } from 'victory-core';
import type {
    SummaryLapsGenderStudentsEntity,
    SummaryLapsGenderStudentsItemEntity,
} from '@modules/summary-results/entities';

type PlatoonsPieChartProps = {
    data: SummaryLapsGenderStudentsEntity[];
};

const Root = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;

    ${breakpoints.down(1360)} {
        width: 800px;
    }

    ${breakpoints.down(650)} {
        width: 100%;
    }
`;

const Chart = styled.div`
    width: 220px;
    height: 220px;
    z-index: 99;

    svg:not(:root) {
        overflow: visible;
    }

    ${breakpoints.down(650)} {
        width: 100%;
    }
`;

const ProgressBlocks = styled.div<{ columns: number }>`
    display: flex;
    flex-wrap: wrap;
    width: calc(100% - 260px);
    margin-inline-start: auto;

    ${breakpoints.down(650)} {
        width: 100%;
        margin: 16px auto 0 auto;
    }

    > * {
        ${p =>
            (p.columns === 2 &&
                css`
                    width: calc(50% - 16px);
                `) ||
            (p.columns === 3 &&
                css`
                    width: calc(100% / 3 - 16px);
                `)}
    }
`;

const Progress = styled.div`
    margin-inline-start: auto;

    > h4 {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        width: 100%;
        color: ${black[60]};
        font-size: 1.4rem;
        font-weight: 400;
    }
`;

const ProgressList = styled.div`
    margin-top: 16px;

    > * {
        margin-bottom: 16px;
    }
`;

const getPieStudentsItems = (
    items: SummaryLapsGenderStudentsItemEntity[],
    colors: Record<number, string>,
) =>
    items.map(item => {
        // Если не указан возраст
        if (item.age < 0) {
            const colorValues = Object.values(colors);

            return {
                ...item,
                age: i18n.t('common:blocks.common.age_not_defined'),
                fill: colorValues[colorValues.length - 1],
            };
        }

        return {
            ...item,
            fill: colors[item.age],
        };
    });

const PlatoonsPieChart = (props: PlatoonsPieChartProps) => {
    const { data } = props;

    const { t } = useTranslation(['common', 'summary']);

    const [tab, setTab] = React.useState(0);

    const handleChangeTab = (_: React.ChangeEvent<unknown>, value: number): void => setTab(value);

    const studentsByRegion = data[tab];

    const maleStudents = studentsByRegion.sections.find(section => section.gender === Gender.male);
    const pieMaleStudentsItems = getPieStudentsItems(maleStudents?.items ?? [], maleColorsByAge);

    const femaleStudents = studentsByRegion.sections.find(
        section => section.gender === Gender.female,
    );
    const pieFemaleStudentsItems = getPieStudentsItems(
        femaleStudents?.items ?? [],
        femaleColorsByAge,
    );

    const nogenderStudents = studentsByRegion.sections.find(section => section.gender === '');
    const nogenderStudentsItems = getPieStudentsItems(
        nogenderStudents?.items ?? [],
        nogenderColorsByAge,
    );

    const summaryStudentsCount = studentsByRegion.summary;

    const genderItems = [
        ...pieMaleStudentsItems,
        ...pieFemaleStudentsItems,
        ...nogenderStudentsItems,
    ];
    const hasGenderItems = genderItems.length > 0;
    const hasNoGenderItems = nogenderStudentsItems.length > 0;

    const pieData = hasGenderItems ? genderItems : [{ age: '1', count: '1', fill: gray[30] }];

    const pieProps = React.useMemo(
        () => ({
            x: 'age',
            y: 'count',
            innerRadius: 80,
            height: 220,
            padding: 10,
            padAngle: 2,
            labels: pieData.map(dataItem => {
                const label =
                    typeof dataItem.age === 'number'
                        ? CommonHelper.createCountFormatter(dataItem.age, [
                              t('common:blocks.age.one'),
                              t('common:blocks.age.some'),
                              t('common:blocks.age.multiple'),
                          ])
                        : dataItem.age;

                const valueLabel = dataItem.count.toLocaleString().replaceAll(',', ' ');

                return `${label}: ${valueLabel}`;
            }),
            style: {
                data: { fill: ({ datum }: PieDataCallbackArgs) => datum.fill },
            },
        }),
        [t, studentsByRegion, pieData],
    );

    const labelProps = React.useMemo(
        () => ({
            direction: 'ltr',

            text: [
                summaryStudentsCount.toLocaleString().replaceAll(',', ' '),
                CommonHelper.createCountFormatter(
                    summaryStudentsCount,
                    [
                        t('common:blocks.students.one'),
                        t('common:blocks.students.some'),
                        t('common:blocks.students.multiple'),
                    ],
                    { withNumber: false },
                ),
            ],

            style: [
                {
                    paddingTop: 6,
                    fill: 'rgba(0,0,0,.87)',
                    fontSize: 34,
                    fontWeight: 600,
                    fontFamily: muiTheme.typography.fontFamily.toString(),
                },
                {
                    fill: black[54],
                    fontSize: 14,
                    fontFamily: muiTheme.typography.fontFamily.toString(),
                },
            ],
        }),
        [t, summaryStudentsCount],
    );

    return (
        <Root>
            <TabsNavigation value={tab} onChange={handleChangeTab}>
                {data.map((dataItem, idx) => {
                    const label =
                        dataItem.regionName === 'all'
                            ? t('summary:regions.all')
                            : dataItem.regionName;

                    return <TabNavigation key={dataItem.regionName} label={label} value={idx} />;
                })}
            </TabsNavigation>

            <Chart>
                <PieChart data={pieData} PieProps={pieProps} LabelProps={labelProps} />
            </Chart>

            <ProgressBlocks columns={hasNoGenderItems ? 3 : 2}>
                <Progress>
                    <h4>
                        <span>{t('summary:gender.male')}</span>
                        <span>{maleStudents?.summary ?? 0}</span>
                    </h4>

                    <ProgressList>
                        {pieMaleStudentsItems.map(item => {
                            if (!item) {
                                return null;
                            }

                            const label =
                                typeof item.age === 'number'
                                    ? CommonHelper.createCountFormatter(item.age, [
                                          t('common:blocks.age.one'),
                                          t('common:blocks.age.some'),
                                          t('common:blocks.age.multiple'),
                                      ])
                                    : item.age;

                            const valueLabel = item.count.toLocaleString().replaceAll(',', ' ');

                            return (
                                <ProgressItem
                                    key={item.age}
                                    label={label}
                                    valueLabel={valueLabel}
                                    value={item.count}
                                    maxValue={summaryStudentsCount}
                                    lineColor={item.fill}
                                />
                            );
                        })}
                    </ProgressList>
                </Progress>

                <Progress>
                    <h4>
                        <span>{t('summary:gender.female')}</span>
                        <span>{femaleStudents?.summary ?? 0}</span>
                    </h4>

                    <ProgressList>
                        {pieFemaleStudentsItems.map(item => {
                            if (!item) {
                                return null;
                            }

                            const label =
                                typeof item.age === 'number'
                                    ? CommonHelper.createCountFormatter(item.age, [
                                          t('common:blocks.age.one'),
                                          t('common:blocks.age.some'),
                                          t('common:blocks.age.multiple'),
                                      ])
                                    : item.age;

                            const valueLabel = item.count.toLocaleString().replaceAll(',', ' ');

                            return (
                                <ProgressItem
                                    key={item.age}
                                    label={label}
                                    valueLabel={valueLabel}
                                    value={item.count}
                                    maxValue={summaryStudentsCount}
                                    lineColor={item.fill}
                                />
                            );
                        })}
                    </ProgressList>
                </Progress>

                {hasNoGenderItems ? (
                    <Progress>
                        <h4>
                            <span>{t('common:blocks.common.gender_not_defined')}</span>
                            <span>{nogenderStudents?.summary ?? 0}</span>
                        </h4>

                        <ProgressList>
                            {nogenderStudentsItems.map(item => {
                                if (!item) {
                                    return null;
                                }

                                const label =
                                    typeof item.age === 'number'
                                        ? CommonHelper.createCountFormatter(item.age, [
                                              t('common:blocks.age.one'),
                                              t('common:blocks.age.some'),
                                              t('common:blocks.age.multiple'),
                                          ])
                                        : item.age;

                                const valueLabel = item.count.toLocaleString().replaceAll(',', ' ');

                                return (
                                    <ProgressItem
                                        key={item.age}
                                        label={label}
                                        valueLabel={valueLabel}
                                        value={item.count}
                                        maxValue={summaryStudentsCount}
                                        lineColor={item.fill}
                                    />
                                );
                            })}
                        </ProgressList>
                    </Progress>
                ) : null}
            </ProgressBlocks>
        </Root>
    );
};

export { PlatoonsPieChart };
